import React, {useContext, useEffect, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../utils/webInfo";
import callToast, {
    handleChangeData,
    mergeWithSelected,
    postData,
    validateFormFields
} from "../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../Components/PageHeading";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import {TrackingDevice} from "../../../../../../types/demo";
import {Dropdown} from "primereact/dropdown";


const VehicleTrackingDevices = () => {
    const initialState: TrackingDevice = {
        sl_number: '',
        model: '',
        protocol: '',
        vehicle_device_vendor_id: null,
    };

    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<TrackingDevice>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    const [vendorNameFilter, setVendorNameFilter] = useState<string>('');
    const [vendorOptions, setVendorOptions] = useState<{ id: number, name: string }[]>([]);

    // pagination
    const {page, limitData, handlePageChange} = usePagination();

    // endpoint
    const mainTitle = "Vehicle Tracking Devices"
    const tableDataEndpointUrl = `/vehicle-tracking-devices?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-tracking-devices/${updatedId}` : "/vehicle-tracking-devices";

    // fetch data
    const {
        data: tableDataList,
        totalData, loading
    } = useFetch(tableDataEndpointUrl, [refresh])

    // fetch dorpdown data
    // parking campus
    const {data: vendorRowData} = useFetch(
        `/vehicle-device-vendors?page=1&per_page=10${vendorNameFilter ? `&name=${vendorNameFilter}` : ''}`,
        [vendorNameFilter]
    );
    useEffect(() => {
        if (vendorRowData) {
            const modifiedData = vendorRowData?.map((item: any) => ({
                id: item?.id,
                name: item?.name
            }));
            setVendorOptions(mergeWithSelected(modifiedData, formData?.vehicle_device_vendor_id));
        }
    }, [vendorRowData, formData?.vehicle_device_vendor_id]);







    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            { field: 'sl_number', label: 'Serial Number' },
            { field: 'model', label: 'Model' },
            { field: 'protocol', label: 'Protocol' },
            { field: 'vehicle_device_vendor_id', label: 'Vendor'}
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            sl_number: formData.sl_number.replace(/\s+/g, ' ').trim(),
            model: formData.model.replace(/\s+/g, ' ').trim(),
            protocol: formData.protocol.replace(/\s+/g, ' ').trim(),
            vehicle_device_vendor_id: formData.vehicle_device_vendor_id?.id,
        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));


            if (response.ok) {
                callToast(toast, true, `Vehicle ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState)
                setModal(false)
                setUpdatedId(null)
                setRefresh(!refresh);
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }

        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }

    }

    // row edit template
    const actionTemplate = (rowData: any) => {

        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button icon={'pi pi-pencil'} className={'border-rounded w-2rem h-2rem'} onClick={() => {

                    setModal(true)
                    setUpdatedId(rowData?.id)
                    setFormData({
                        sl_number: rowData.sl_number,
                        model: rowData.model,
                        protocol: rowData.protocol,
                        vehicle_device_vendor_id: rowData.vehicle_device_vendor
                    })
                }} outlined/>
            </div>
        )
    }



    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading title={mainTitle}
                         isCreate={true}
                         createFunction={() => setModal(true)}
                         back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column
                    field="sl_number"
                    header="serial Number"
                    style={{width: '25%'}}
                    headerClassName={"white-space-nowrap"}/>
                <Column
                    field="model"
                    header="Model"
                    style={{width: '25%'}}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    field="protocol"
                    header="Protocol"
                    style={{width: '25%'}}
                    headerClassName={"white-space-nowrap"}
                />

                <Column
                    field="vehicle_device_vendor.name"
                    header="Vendor Name"
                    style={{width: '25%'}}
                    headerClassName={"white-space-nowrap"}
                />

                <Column header={"Action"} rowEditor={true}
                        bodyStyle={{textAlign: 'center'}}
                        body={actionTemplate}
                        headerClassName={"flex justify-content-end"}
                />

            </DataTable>

            <Dialog visible={modal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setUpdatedId(null)
                        setModal(false)
                        setFormData(initialState)
                    }}
            >
                <div>
                    <form className={''}
                          onSubmit={handleSubmit}
                    >
                        <div className="grid ">
                            <div className="col-12  md:col-6">
                                <Label label={"Parking Campus"} required={!updatedId}/>
                                <Dropdown
                                    options={vendorOptions? vendorOptions : []}
                                    optionLabel="name"
                                    id="vehicle_device_vendor_id"
                                    name={`vehicle_device_vendor_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_device_vendor_id}
                                    onChange={(e) => handleChangeData('vehicle_device_vendor_id', e.value, formData, setFormData)}
                                    placeholder="Select vendor"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setVendorNameFilter(e.filter)
                                        }, 500);

                                        if (vendorRowData) {
                                            const modifiedData = vendorRowData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name
                                            }));
                                            setVendorOptions(modifiedData);
                                        }
                                    }}
                                    showClear
                                    showFilterClear
                                />
                            </div>

                        </div>

                        <div className="grid mt-2">
                            <div className="col-12  md:col-4">
                                <Label label={"Serial number"} required={!updatedId}/>
                                <InputText
                                    id="sl_number"
                                    value={formData.sl_number}
                                    size={100}
                                    name={"sl_number"}
                                    onChange={(e) => handleChangeData('sl_number', e.target.value, formData, setFormData)}
                                    placeholder={"Enter serial number"}
                                    className={`w-full`}
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Model"} required={!updatedId}/>
                                <InputText
                                    id="model"
                                    value={formData.model}
                                    size={100}
                                    name={"model"}
                                    onChange={(e) => handleChangeData('model', e.target.value, formData, setFormData)}
                                    placeholder={"Enter model"}
                                    className={`w-full`}
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Protocol"} required={!updatedId}/>
                                <InputText
                                    id="protocol"
                                    value={formData.protocol}
                                    size={100}
                                    name={"protocol"}
                                    onChange={(e) => handleChangeData('protocol', e.target.value, formData, setFormData)}
                                    placeholder={"Enter protocol"}
                                    className={`w-full`}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={updatedId ? 'Update' : "Create"}
                                className="p-button-primary"
                                size={'small'}
                                loading={isSubmitting}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>

        </div>
    );
};

export default VehicleTrackingDevices;

